import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { fetchAccountsReceivableBalanceForManager, fetchSalesAccountsReceivable, updateAdminAccount } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { LoaderContextCreator } from '@/contexts/loader'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import dayjs from 'dayjs'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { useDayjsRange } from '@/hooks/use-dayjs-range'
import { InputField } from '@/components/molecules/input-field'
import { TypeLabel } from './type-label'
import { AccountsReceivableBalance } from '@/models/accounts-receivable-manager'

type Props = {
  value: string
  onCancel: () => void
  onSave: () => void
}

export const AggregatePeriodModal: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { dayjsRange } = useDayjsRange()
  const viewMonthRange = dayjsRange({
    start: dayjs().startOf('month').subtract(5, 'month'),
    end: dayjs().endOf('month'),
    interval: 1,
    unit: 'month',
  }).reverse()

  const [selectedMonth, setSelectedMonth] = useState(props.value)
  const [accountsReceivableMaster, setAccountsReceivableMaster] = useState<Array<{ code: string; name: string }>>()
  const [searchResponse, setSearchResponse] = useState<
    Pick<AccountsReceivableBalance, 'customerCode' | 'isDiscount' | 'prevBalancePrice'>[]
  >([])

  const onSave = async () => {
    try {
      setIsLoading(true)
      await updateAdminAccount({ accountsReceivable: { aggregatePeriod: selectedMonth, defaultPrevBalancePrice: searchResponse } })
      props.onSave()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const onChangePrice = (params: { customerCode: string; isDiscount: boolean; prevBalancePrice: number }) => {
    const isNotExistsBalance = pickAccountsReceivableBalance(params.customerCode, params.isDiscount) === undefined
    if (isNotExistsBalance) {
      setSearchResponse([...searchResponse, params])
    } else {
      setSearchResponse(
        searchResponse?.map(res => {
          const isEditingItem = res.customerCode === params.customerCode && res.isDiscount === params.isDiscount
          const newPrice = isEditingItem ? params.prevBalancePrice : res.prevBalancePrice
          return { ...res, prevBalancePrice: newPrice }
        }),
      )
    }
  }

  const doSearch = async (searchCondition: { salesDate: string }) => {
    try {
      setIsLoading(true)
      const res = await fetchAccountsReceivableBalanceForManager(searchCondition)
      setSearchResponse(res?.results || [])
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const pickAccountsReceivableBalance = (customerCode: string, isDiscount: boolean) => {
    return searchResponse?.find(res => res.customerCode === customerCode && res.isDiscount === isDiscount)
  }

  useEffect(() => {
    fetchSalesAccountsReceivable().then(res => {
      if (!res) {
        return
      }
      setAccountsReceivableMaster(res.accountsReceivables)
    })
  }, [])

  useEffect(() => {
    doSearch({ salesDate: selectedMonth })
  }, [selectedMonth])

  return (
    <Modal customCss={modalStyle}>
      <ModalHeader>売掛金の計算開始月を設定</ModalHeader>
      <ModalBody>
        <div className="modal-body">
          <div className="sub-description-block">
            <div className="icon">
              <img src={require('@/static/images/icon_warning.svg')} />
            </div>
            <div>
              <div className="sub-title">注意事項</div>
              <div className="sub-summary">
                設定した月以降の売掛金を計算します。計算開始月の前月繰越額（計算開始月前月の「当月残高」）を設定してください。
              </div>
            </div>
          </div>

          <div className="input-field">
            <div className="input-title">計算開始月</div>
            <Select value={selectedMonth} onChange={v => setSelectedMonth(v.toString())}>
              {viewMonthRange.map(d => (
                <AntSelect.Option key={d.format('YYYY-MM-DD')} value={d.format('YYYY-MM-DD')}>
                  {d.format(t('YYYY-MM'))}
                </AntSelect.Option>
              ))}
            </Select>
          </div>

          <div css={carryoverPriceStyle} className="input-field">
            <div className="input-title">各売掛先の前月繰越額を設定</div>
            <div className="table-block">
              <div>
                <div className="table-header">
                  <div className="table-row">
                    <td className="table-cell col-name">売掛先</td>
                    <td className="table-cell col-type">種別</td>
                    <td className="table-cell col-price">前月繰越額（円）</td>
                  </div>
                </div>
                <div className="table-body">
                  {accountsReceivableMaster?.map(responseRow => (
                    <>
                      <div className="table-row" key={`agg-period-${responseRow.code}-${responseRow.name}-sale`}>
                        <td className="table-cell col-name">{responseRow.name}</td>
                        <td className="table-cell col-type">
                          <TypeLabel isDiscount={false} />
                        </td>
                        <td className="table-cell col-price">
                          <InputField
                            width="135px"
                            value={pickAccountsReceivableBalance(responseRow.code, false)?.prevBalancePrice || 0}
                            marginBottom={0}
                            onChange={e =>
                              onChangePrice({
                                customerCode: responseRow.code,
                                isDiscount: false,
                                prevBalancePrice: Number(e.target.value),
                              })
                            }
                          />
                        </td>
                      </div>
                      <div className="table-row" key={`agg-period-${responseRow.code}-${responseRow.name}-discount`}>
                        <td className="table-cell col-name">{responseRow.name}</td>
                        <td className="table-cell col-type">
                          <TypeLabel isDiscount={true} />
                        </td>
                        <td className="table-cell col-price">
                          <InputField
                            width="135px"
                            value={pickAccountsReceivableBalance(responseRow.code, true)?.prevBalancePrice || 0}
                            marginBottom={0}
                            onChange={e =>
                              onChangePrice({
                                customerCode: responseRow.code,
                                isDiscount: true,
                                prevBalancePrice: Number(e.target.value),
                              })
                            }
                          />
                        </td>
                      </div>
                    </>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button buttonType={ButtonType.Secondary} height="38px" width="110px" marginRight={16} onClick={props.onCancel}>
          {t('Cancel')}
        </Button>
        <Button buttonType={ButtonType.Primary} height="38px" width="110px" onClick={onSave}>
          {t('Save')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const carryoverPriceStyle = css({
  '.table-block': { marginTop: 0 },
  '.col-name': { flexGrow: 1 },
  '.col-type': { minWidth: 60, maxWidth: 60 },
  '.col-price': { minWidth: 145, maxWidth: 145, textAlign: 'right', input: { textAlign: 'right' } },
})

const modalStyle = css({
  height: 600,
  minHeight: 600,
  maxHeight: 600,
  width: 720,
  minWidth: 720,
  '.modal-body': {
    background: 'white',
    border: '1px solid rgb(204, 204, 204)',
    borderRadius: 5,
    padding: '20px 32px',
  },
  '.sub-description-block': {
    marginBottom: 16,
    padding: '0.75rem',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    display: 'flex',
    gap: 8,
    '.sub-title': { color: '#92400e' },
    '.sub-summary': { lineHeight: 1.5, paddingTop: 8 },
  },
  '.input-field': {
    '.input-title': {
      display: 'flex',
      fontSize: 12,
      fontWeight: 'bold',
      letterSpacing: 0.6,
      paddingBottom: 12,
    },
  },
})
